<template>
  <div class="mayday-article-editor" :class="{ 'image-pointer': !editable }">
    <!-- EDITOR SECTION -->
    <mayday-editor
      v-if="showEditor"
      ref="article-editor"
      id="mayday-editor"
      v-model="content"
      :editable="editable"
      :options="options"
      :generate-translation="fetchTranslationBuffer"
      :generate-grammar="fetchCorrectionBuffer"
      :generate-style="fetchStyleBuffer"
      @update="onUpdate"
      @open-link-modal="openLinkModal"
      @click:inline-content="handleOnClickInlineContent"
      @click:attached-file="handleClickAttachedFile"
      @click:image="handleOnImageClick($event.src)"
      @click:focus-hash="handleOnFocusHash"
      @click:copy="handleOnCopy"
      @click:open-link="handleOpenLink"
      @generate-summary="$emit('generate-summary', $event)"
      @generate-action-items="$emit('generate-action-items')"
    />
    <LightBox v-show="!!imageUrl" :image-url.sync="imageUrl" />

    <MaydayEditorOpenLinkModal
      v-if="linkModal.display"
      :display="linkModal.display"
      :src="linkModal.src"
      @close="closeLinkModal"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

import MaydayEditorOpenLinkModal from './utils/MaydayEditorOpenLinkModal';
import LightBox from '@/components/LightBox';

export default {
  name: 'MaydayArticleEditor',
  components: {
    MaydayEditorOpenLinkModal,
    LightBox,
  },
  props: {
    contentId: String,
    body: String,
    editable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showEditor: true,
      content: '',
      linkModal: {
        display: false,
        src: '',
      },
      imageUrl: '',
      options: {
        fontOptions: true,
        extraFontOptions: true,
        colorOptions: true,
        structurationOptions: true,
        extraStructurationOptions: true,
        tableOptions: true,
        richOptions: true,
        tocClickOptions: false,
        allowToc: true,
        allowSummary: false,
        allowTranslation: false,
        allowGrammarCorrection: false,
        allowToneImprovement: false,
      },
    };
  },
  created() {
    this.setEditorContent(this.body);
    setTimeout(() => {
      const hash = this.$route.hash;
      this.handleOnFocusHash(hash);
    }, 1000);
    this.options.allowSummary = this.companyAllowSummaryPreference;
    this.options.allowTranslation = this.companyAllowTranslationPreference;
    this.options.allowGrammarCorrection =
      this.companyAllowGrammarCorrectionPreference;
    this.options.allowToneImprovement =
      this.companyAllowToneImprovementPreference;
    this.options.allowActionItems = this.companyAllowActionItemsPreference;
  },
  mounted() {
    this.$root.$on('IMPORTED_ARTICLE_EDITOR_REFRESH', () => {
      this.resetEditor();
    });
    this.$root.$on('content-restored', () => {
      this.resetEditor();
    });
  },
  computed: {
    ...mapGetters([
      'companyAllowSummaryPreference',
      'companyAllowTranslationPreference',
      'companyAllowGrammarCorrectionPreference',
      'companyAllowToneImprovementPreference',
      'companyAllowActionItemsPreference',
    ]),
  },
  methods: {
    handleOpenLink({ href }) {
      window.open(href, '_blank');
    },
    resetEditor() {
      this.showEditor = false;

      this.$nextTick(() => {
        this.showEditor = true;
      });
      this.setEditorContent(this.body);
    },
    onUpdate(body) {
      this.$emit('update-written-content', { body });
    },
    setEditorContent(rawContent) {
      this.showEditor = false;
      this.$nextTick(() => {
        this.showEditor = true;
        const maydayEditor = this.$refs['article-editor'];
        this.content = rawContent;
        if (maydayEditor) {
          maydayEditor.clearHistory();
          maydayEditor.editor.commands.setContent(this.content);
        }
      });
    },
    openLinkModal(src) {
      this.linkModal.display = true;
      this.linkModal.src = src;
    },
    closeLinkModal() {
      this.linkModal.display = false;
      this.linkModal.src = '';
    },
    handleClickAttachedFile(evt) {
      if (evt && evt.href) {
        window.open(evt.href, '_blank');
      }
    },
    handleOnImageClick(src) {
      if (!this.editable && src) {
        this.imageUrl = src;
      }
    },
    async *fetchTranslationBuffer(text, language, abortController) {
      for await (const chunk of await this.fetchTranslation({
        text,
        language,
        abortController,
      })) {
        yield chunk;
      }
    },
    async *fetchCorrectionBuffer(text, _, abortController) {
      for await (const chunk of await this.fetchCorrection({
        text,
        abortController,
        correctionType: 'grammar',
      })) {
        yield chunk;
      }
    },
    async *fetchStyleBuffer(text, _, abortController) {
      for await (const chunk of await this.fetchCorrection({
        text,
        abortController,
        correctionType: 'style',
      })) {
        yield chunk;
      }
    },
    handleOnFocusHash(hash) {
      if (!hash) return;
      const id = hash.slice(1);

      const uniqueNode = document.querySelector(
        `[data-uid="${id}"], [id="${id}"]`,
      );
      if (!uniqueNode) return;
      uniqueNode.scrollIntoView({ behavior: 'smooth' });
    },
    handleOnCopy(uniqueId) {
      const url = new URL(window.location.href);
      navigator.clipboard.writeText(`${url.origin}${url.pathname}#${uniqueId}`);

      this.$message(this.$t('editor.copy'));
    },
    ...mapActions('knowledgeModule', ['handleOnClickInlineContent']),
    ...mapActions('aiSearchModule', ['fetchTranslation', 'fetchCorrection']),
  },
  watch: {
    editable() {
      this.resetEditor();
    },
    contentId: {
      handler(val, oldVal) {
        if (oldVal !== val) {
          this.resetEditor();
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
:deep() .mayday-editor {
  padding: 0;
}

:deep().mayday-editor__slash-menu-command-button {
  z-index: 1;
}

.image-pointer {
  :deep() .mayday-editor {
    &__image-wrapper {
      img {
        cursor: pointer;
      }
    }
  }
}

:deep() summary {
  color: black;
}
</style>
